import {
  deleteApiV5MessagesIdDiscard,
  patchApiV5MessagesId,
  patchApiV5MessagesIdParticipants,
  postApiV5Messages,
  postApiV5MessagesIdSend,
} from '@liscio/api';

const positiveInteger = (value, errorMessage = 'One or more recipients are unavailable.') => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error(errorMessage);
  }
  return parsed;
};

const attachmentOrigin = (document) => {
  if (['direct_upload', 'liscio_vault', 'template'].includes(document.attachment_origin)) {
    return document.attachment_origin;
  }
  if (document.isAttachmentFromEmailOrMessage) return null;
  if (document.fromlisciovault) return 'liscio_vault';
  if (document.from_template_document || document.is_template) return 'template';
  return 'direct_upload';
};

export const normalizeEmailReplyData = ({
  subject,
  content,
  notifyViaText,
  selectedRecords = [],
  documents = [],
  emailId,
}) => {
  const participants = selectedRecords.map((record) => {
    const id = positiveInteger(record.contactId);
    const accountId = Number(record.accountId);
    const type = record.assigneeType === 'contact' ? 'contact' : 'user';

    return {
      id,
      ...(type === 'user' ? { cpa_user_id: id } : {}),
      type,
      is_recipient: true,
      ...(Number.isInteger(accountId) && accountId > 0
        ? { account_id: accountId }
        : {}),
    };
  });
  const accountIds = [
    ...new Set(
      participants
        .map(({ account_id: accountId }) => accountId)
        .filter(Boolean),
    ),
  ];
  const documentIds = [
    ...new Set(documents.map(({ id }) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)),
  ];
  const attachmentOrigins = documentIds.map((documentId) => {
    const document = documents.find(({ id }) => Number(id) === documentId);
    return { document_id: documentId, origin: attachmentOrigin(document) };
  });

  return {
    subject: subject || '',
    content: content || '',
    notifyViaText: Boolean(notifyViaText),
    participants,
    // Draft persistence cannot provision uninvited Contacts; Send receives the full set.
    draftParticipants: participants
      .filter(({ type }) => type === 'user')
      .map(({ id, type, is_recipient, account_id: accountId }) => ({
        id,
        type,
        is_recipient,
        ...(accountId ? { account_id: accountId } : {}),
      })),
    accountIds,
    documentIds,
    attachmentOrigins: attachmentOrigins.some(({ origin }) => origin !== null)
      ? attachmentOrigins
      : undefined,
    emailId: emailId == null || emailId === ''
      ? undefined
      : positiveInteger(emailId, 'The source email is unavailable.'),
  };
};

const draftBody = (data, messageThreadId) => ({
  thread: {
    title: data.subject,
    status: 'Draft',
    is_notify_via_text: data.notifyViaText,
  },
  messages: {
    content: data.content,
    status: 'Draft',
    ...(messageThreadId ? { message_thread_id: messageThreadId } : {}),
  },
  doc_ids: data.documentIds,
  ...(data.attachmentOrigins
    ? { attachment_origins: data.attachmentOrigins }
    : {}),
});

export default class EmailReplyV5Lifecycle {
  constructor({ onDraftCreated } = {}) {
    this.onDraftCreated = onDraftCreated;
    this.messageId = undefined;
    this.messageThreadId = undefined;
    this.pendingDraft = null;
    this.queue = Promise.resolve();
    this.generation = 0;
  }

  syncDraft(messageId, messageThreadId) {
    if (messageId && !this.messageId) {
      this.messageId = messageId;
      this.messageThreadId = messageThreadId;
    }
  }

  enqueue(operation) {
    const generation = this.generation;
    const queued = this.queue.then(operation, operation).then((result) => {
      if (generation !== this.generation) {
        throw new Error('The active draft changed before the operation completed.');
      }
      return result;
    });
    this.queue = queued.then(
      () => undefined,
      () => undefined,
    );
    return queued;
  }

  async ensureDraft(data) {
    if (this.messageId) return this.messageId;
    if (this.pendingDraft) return this.pendingDraft;

    const generation = this.generation;
    const pendingDraft = postApiV5Messages(draftBody(data)).then((response) => {
      if (
        generation !== this.generation ||
        !response.message_id ||
        !response.message_thread_id
      ) {
        throw new Error('Failed to create draft.');
      }
      this.messageId = response.message_id;
      this.messageThreadId = response.message_thread_id;
      this.onDraftCreated?.({
        messageId: this.messageId,
        messageThreadId: this.messageThreadId,
      });
      return this.messageId;
    }).finally(() => {
      if (this.pendingDraft === pendingDraft) this.pendingDraft = null;
    });
    this.pendingDraft = pendingDraft;
    return pendingDraft;
  }

  persist(data) {
    return this.enqueue(async () => {
      const messageId = await this.ensureDraft(data);
      await patchApiV5MessagesId(
        messageId,
        draftBody(data, this.messageThreadId),
      );
      await patchApiV5MessagesIdParticipants(messageId, {
        account_ids: data.accountIds,
        participants: data.draftParticipants,
      });
      return {
        messageId,
        messageThreadId: this.messageThreadId,
      };
    });
  }

  send(data) {
    return this.enqueue(async () => {
      const messageId = await this.ensureDraft(data);
      await patchApiV5MessagesId(
        messageId,
        draftBody(data, this.messageThreadId),
      );
      await patchApiV5MessagesIdParticipants(messageId, {
        account_ids: data.accountIds,
        participants: data.draftParticipants,
      });
      const response = await postApiV5MessagesIdSend(messageId, {
        thread: {
          title: data.subject,
          is_notify_via_text: data.notifyViaText,
        },
        messages: { content: data.content },
        participants: data.participants,
        ...(data.emailId ? { email_id: data.emailId } : {}),
      });
      return {
        response,
        messageId,
        messageThreadId: this.messageThreadId,
      };
    });
  }

  discard() {
    const queued = this.queue.then(async () => {
      const messageId = this.messageId || await this.pendingDraft;
      const response = messageId
        ? await deleteApiV5MessagesIdDiscard(messageId)
        : undefined;
      this.generation += 1;
      this.messageId = undefined;
      this.messageThreadId = undefined;
      return response;
    });
    this.queue = queued.then(
      () => undefined,
      () => undefined,
    );
    return queued;
  }
}
